import {
  AuthResponse,
  CreditCardDto,
  EditExpenseModel,
  ExpenseDto,
  ExpenseType,
  EntryDto,
  MonthlyClosingDto,
  MonthlyClosingSummaryDto,
  MonthlyForecastDto,
  NewCreditCard,
  NewEntry,
  NewInstallmentExpense,
  NewRecurringExpense,
  SaveClosingDraftCard,
  SaveClosingDraftCommitment,
  UserDto
} from './types';

const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
const tokenStorageKey = 'devoquanto.auth.token';

export function getAuthToken() {
  return localStorage.getItem(tokenStorageKey);
}

export function clearSession() {
  localStorage.removeItem(tokenStorageKey);
}

function saveSession(token: string) {
  localStorage.setItem(tokenStorageKey, token);
}

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getAuthToken();
  const headers = new Headers(options.headers ?? {});

  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers
  });

  if (res.status === 401) {
    clearSession();
  }

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Erro ${res.status}`);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const responseText = await res.text();
  if (!responseText) {
    return null as T;
  }

  return JSON.parse(responseText) as T;
}

export async function loginWithGoogle(idToken: string): Promise<AuthResponse> {
  const auth = await api<AuthResponse>('/api/auth/google', {
    method: 'POST',
    body: JSON.stringify({ idToken })
  });

  saveSession(auth.token);
  return auth;
}

export const getMe = () => api<UserDto>('/api/auth/me');

export const getExpenses = (year?: number, month?: number) => {
  if (year && month) {
    return api<ExpenseDto[]>(`/api/expenses?year=${year}&month=${month}`);
  }
  return api<ExpenseDto[]>('/api/expenses');
};

export const getEntries = (year: number, month: number) =>
  api<EntryDto[]>(`/api/entries?year=${year}&month=${month}`);

export const getEntriesTotal = (year: number, month: number) =>
  api<{ year: number; month: number; total: number }>(`/api/entries/total?year=${year}&month=${month}`);
export const getFinishedInstallments = (year: number, month: number) =>
  api<string[]>(`/api/expenses/finished-installments?year=${year}&month=${month}`);

export const addInstallment = (payload: NewInstallmentExpense) =>
  api<ExpenseDto>('/api/expenses/installments', {
    method: 'POST',
    body: JSON.stringify({
      ...payload,
      purchaseDate: payload.purchaseDate || null,
      dueDate: payload.dueDate || null,
      creditCardId: payload.creditCardId || null
    })
  });

export const addRecurring = (payload: NewRecurringExpense) =>
  api<ExpenseDto>('/api/expenses/recurring', {
    method: 'POST',
    body: JSON.stringify({
      ...payload,
      creditCardId: payload.creditCardId || null
    })
  });

export const toggleExpense = (id: string) =>
  api<ExpenseDto>(`/api/expenses/${id}/toggle`, { method: 'PATCH' });

export const setExpenseStatus = (id: string, isActive: boolean) =>
  api<ExpenseDto>(`/api/expenses/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ isActive })
  });

export const deleteExpense = (id: string) =>
  api<void>(`/api/expenses/${id}`, { method: 'DELETE' });

export const updateExpense = (payload: EditExpenseModel) =>
  api<ExpenseDto>(`/api/expenses/${payload.id}`, {
    method: 'PUT',
    body: JSON.stringify({
      ...payload,
      creditCardId: payload.creditCardId || null
    })
  });

export const addEntry = (payload: NewEntry) =>
  api<EntryDto>('/api/entries', {
    method: 'POST',
    body: JSON.stringify(payload)
  });

export const deleteEntry = (id: string) =>
  api<void>(`/api/entries/${id}`, { method: 'DELETE' });

export const openMonthlyClosing = (year: number, month: number) =>
  api<MonthlyClosingDto>(`/api/monthly-closings/${year}/${month}/draft`, { method: 'POST' });

export const getLatestMonthlyClosing = () =>
  api<MonthlyClosingSummaryDto | null>('/api/monthly-closings/latest', { cache: 'no-store' });

export const addFinancialAdjustment = (year: number, month: number, description: string, amount: number) =>
  api<MonthlyClosingDto>(`/api/monthly-closings/${year}/${month}/adjustments`, {
    method: 'POST',
    body: JSON.stringify({ description, amount })
  });

export const deleteFinancialAdjustment = (year: number, month: number, id: string) =>
  api<MonthlyClosingDto>(`/api/monthly-closings/${year}/${month}/adjustments/${id}`, { method: 'DELETE' });

export const updateClosingCommitment = (
  year: number,
  month: number,
  expenseId: string,
  amount: number,
  isIncluded: boolean,
  updateFutureAmount: boolean,
  paidAnotherWay: boolean,
  paymentMethodNote?: string | null
) =>
  api<MonthlyClosingDto>(`/api/monthly-closings/${year}/${month}/commitments/${expenseId}`, {
    method: 'PUT',
    body: JSON.stringify({ amount, isIncluded, updateFutureAmount, paidAnotherWay, paymentMethodNote })
  });

export const saveMonthlyClosingDraft = (
  year: number,
  month: number,
  commitments: SaveClosingDraftCommitment[],
  adjustments: { description: string; amount: number }[],
  cards: SaveClosingDraftCard[]
) =>
  api<MonthlyClosingDto>(`/api/monthly-closings/${year}/${month}/draft`, {
    method: 'PUT',
    body: JSON.stringify({ commitments, adjustments, cards })
  });

export const updateClosingCard = (
  year: number,
  month: number,
  creditCardId: string,
  amount: number,
  isIncluded: boolean,
  isPaid: boolean,
  paymentMethodNote?: string | null
) =>
  api<MonthlyClosingDto>(`/api/monthly-closings/${year}/${month}/cards/${creditCardId}`, {
    method: 'PUT',
    body: JSON.stringify({ amount, isIncluded, isPaid, paymentMethodNote })
  });

export const finalizeMonthlyClosing = (year: number, month: number) =>
  api<MonthlyClosingDto>(`/api/monthly-closings/${year}/${month}/finalize`, { method: 'POST' });

export const reopenMonthlyClosing = (year: number, month: number) =>
  api<MonthlyClosingDto>(`/api/monthly-closings/${year}/${month}/reopen`, { method: 'POST' });

export const typeLabel = (type: ExpenseType) => {
  switch (type) {
    case ExpenseType.Installment:
      return 'Parcelamento/Financiamento';
    case ExpenseType.RecurringCard:
      return 'Assinatura no cartão';
    case ExpenseType.RecurringOffCard:
      return 'Pagamentos manuais';
    default:
      return 'Outro';
  }
};

export const typeClass = (type: ExpenseType) => {
  switch (type) {
    case ExpenseType.Installment:
      return 'pill-blue';
    case ExpenseType.RecurringCard:
      return 'pill-green';
    case ExpenseType.RecurringOffCard:
      return 'pill-amber';
    default:
      return 'pill-muted';
  }
};

export const getMonthlyForecast = (year: number, month: number) =>
  api<MonthlyForecastDto>(`/api/monthly-closings/forecast?year=${year}&month=${month}`, { cache: 'no-store' });

export const getCreditCards = () =>
  api<CreditCardDto[]>('/api/credit-cards');

export const addCreditCard = (payload: NewCreditCard) =>
  api<CreditCardDto>('/api/credit-cards', {
    method: 'POST',
    body: JSON.stringify({
      name: payload.name,
      brand: payload.brand || null,
      lastFourDigits: payload.lastFourDigits || null,
      limit: payload.limit === '' || payload.limit === undefined ? null : Number(payload.limit),
      closingDay: payload.closingDay === '' || payload.closingDay === undefined ? null : Number(payload.closingDay),
      dueDay: payload.dueDay === '' || payload.dueDay === undefined ? null : Number(payload.dueDay),
      isActive: payload.isActive ?? true
    })
  });

export const updateCreditCard = (id: string, payload: NewCreditCard & { isActive: boolean }) =>
  api<CreditCardDto>(`/api/credit-cards/${id}`, {
    method: 'PUT',
    body: JSON.stringify({
      name: payload.name,
      brand: payload.brand || null,
      lastFourDigits: payload.lastFourDigits || null,
      limit: payload.limit === '' || payload.limit === undefined ? null : Number(payload.limit),
      closingDay: payload.closingDay === '' || payload.closingDay === undefined ? null : Number(payload.closingDay),
      dueDay: payload.dueDay === '' || payload.dueDay === undefined ? null : Number(payload.dueDay),
      isActive: payload.isActive
    })
  });

export const toggleCreditCard = (id: string) =>
  api<CreditCardDto>(`/api/credit-cards/${id}/toggle`, { method: 'PATCH' });

export const deleteCreditCard = (id: string) =>
  api<void>(`/api/credit-cards/${id}`, { method: 'DELETE' });
